import { Component, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RouterLink } from '@angular/router';
import type { MenuItem } from 'primeng/api';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_NAME } from 'app/core/tokens/app-name.token';
import { ButtonModule } from 'primeng/button';
import { IUserMenuOption } from 'app/shared/interfaces/IMainLayout';
import { AuthService } from 'app/core/services/auth/auth.service';
import { USER_ROLE_LABEL_ES } from 'app/core/interfaces/login.interface';
import { NotificationModalComponent } from 'app/features/notification/components/notification-modal/notification-modal.component';
import { UserMenuComponent } from "../user-menu/user-menu.component";
import { initials } from 'app/shared/utils/format.util';
import { NotificationStoreService } from 'app/core/services/notifications/store/notification-store.service';

@Component({
  selector: 'navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, NotificationModalComponent, UserMenuComponent],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent {
  private router = inject(Router);
  readonly appName = inject(APP_NAME);
  readonly authService = inject(AuthService);
  private readonly notifStore = inject(NotificationStoreService);
  readonly initials = initials;
  
  // UserRole = UserRole;
  UserRole = USER_ROLE_LABEL_ES;

  readonly items = signal<MenuItem[]>([]);
  readonly unreadCountChange = signal<number>(0);

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/knowledge-base' };

  readonly notificationsOpen = signal(false);
  @ViewChild(NotificationModalComponent) notificationModal!: NotificationModalComponent;

  readonly userMenuOpen = signal(false);
  @ViewChild(UserMenuComponent) UserMenu!: UserMenuComponent;

  readonly unreadCount = this.notifStore.unreadCount;
  selectedOption = null;

  toggleNotifications(ev: MouseEvent) {
    if (this.notificationsOpen()) {
      this.notificationsOpen.set(false);
      return;
    }

    this.notifStore.unreadCount.set(0);
    this.notificationModal.openFromClick(ev);
  }

  toggleUserMenu(ev: MouseEvent) {
    this.UserMenu.anchor(ev); // solo ancla (posicionamiento)
    this.userMenuOpen.set(!this.userMenuOpen());
  }

  options: IUserMenuOption[] = [
    /*
    {
      id: "1",
      name: 'Mi perfil',
      image: 'profile.svg',
      action: () => console.log("Ir al perfil")
    },
    {
      id: "2",
      name: 'Abrir Suite',
      image: 'suite.svg',
      action: () => console.log("Ir a la suite")
    },
    */
    {
      id: "3",
      name: 'Cerrar sesión',
      image: 'log-out.svg',
      action: () => this.authService.logout()
    },
  ];

  constructor() {
    // Recalcular en cada navegación
    this.notifStore.init();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.items.set(this.build()));

    this.items.set(this.build());
  }

  onNotificationsClosed() {
    window.location.reload();
  }

  private build(): MenuItem[] {
    const out: MenuItem[] = [];
    let url = '';

    let snap: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (snap) {
      // Segmentos de esta rama (puede ser [])
      const seg = (snap.url ?? []).map(s => s.path).join('/');
      if (seg) url += `/${seg}`;

      const d: any = snap.data ?? {};

      // Si esta ruta pertenece a un "grupo" (Configuración), se inserta una sola vez
      if (d.group && !out.some(i => i.label === d.group)) {
        out.push({ label: d.group, icon: d.groupIcon ?? 'pi pi-cog' });
      }

      // Si esta ruta define breadcrumb, lo agregamos con su link acumulado
      if (d.breadcrumb) {
        out.push({
          label: d.breadcrumb,
          routerLink: url || '/', // por si queda vacío
          icon: d.icon, // opcional
        });
      }

      // bajar al primer hijo
      snap = snap.firstChild ?? null;
    }

    return out;
  }
}